import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../util/spinner-shim'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import withAppStore from '../../util/withAppStore'

import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import statusCalculate from 'altrac-application-status'

import styles from '../../../css/devicePage.module.css'
import stylesSettings from '../../../css/settings.module.css'
import { NumberInput } from '../partials/Inputs/Inputs.jsx'
import StatusSave from '../partials/StatusSave.jsx'
import StatusSaveCheck from '../partials/StatusSaveCheck.jsx'

const dispatch = (action, obj = {}) => AppDispatcher.dispatch({ action, ...obj })

let debounceTimeout
const debounceMilliseconds = 1000

const MoistureSetPoints = (props) => {
  const {
    setPoints: propsSetPoints = [undefined],
    changeHandler,
  } = props || {}

  if (propsSetPoints.length === 0) propsSetPoints.push('')

  // const [state, setState] = useState({ setPoints: (setPoints || []) });
  const [setPoints, setSetPoints] = useState(propsSetPoints)

  /**
   * updates local state and parent state via the changeHandler.
   * @param {number} value
   */
  const update = (value) => {
    const filtered = value.filter(el => !!el || el === '')
    setSetPoints(filtered)
    changeHandler(filtered)
  }

  /**
   * Set window focus to a setPoint element after a short delay.
   * @param {number} x - index of setPoint
   */
  const focusInSetPoint = (x) => {
    window.setTimeout(() => {
      document.getElementById(`setPoint-${x}`).focus()
    }, 5)
  }

  const addNewSetPoint = () => {
    const last = setPoints.length - 1

    // only allowed to add 1 empty setpoint input field
    if (setPoints.length && setPoints[last] === '') {
      if (last >= 0) focusInSetPoint(last)
      return
    }

    // limit to 8 setpoints
    if (setPoints.length > 7) {
      focusInSetPoint(last)
      return
    }
    const newSetPoints = [...setPoints, '']
    update(newSetPoints)
    focusInSetPoint(newSetPoints.length - 1)
  }

  const removeSetPoint = (event) => {
    const { target: { id } } = event
    const [, index] = id.split('-')
    const newSetPoints = [...setPoints] // copy the array into a new object
    newSetPoints.splice(index, 1) // remove the indexed setPoint
    if (newSetPoints.length < 1) newSetPoints.push('')
    update(newSetPoints)
  }

  return (
    <div className={stylesSettings.moistureList}>
      <ul style={{ marginBottom: 0 }}>
        {
          setPoints.map((setPoint, index, array) => (
            <li
              key={`setPoint-${setPoint}`}
              style={{ justifyContent: 'normal', paddingBottom: 0 }}
            >
              <div className={stylesSettings.otherNumber} style={{ width: '70%' }}>
                <NumberInput
                  id={`setPoint-${index}`}
                  value={setPoint}
                  min={0}
                  max={100}
                  step={1}
                  style={stylesSettings.otherNumberInput}
                  required
                  childChange={
                    (event) => {
                      const { target: { id, max, min, value: inputValue } } = event
                      const [, x] = id.split('-')
                      const newSetPoints = [...setPoints] // copy the array into a new object
                      let value = Number(inputValue)

                      // value can't be < min
                      if (inputValue.length && value < Number(min)) value = min

                      // value can't be > max
                      if (value > Number(max)) value = inputValue.substr(0, 2)

                      newSetPoints[x] = value

                      // change the displayed value
                      update(newSetPoints)
                      focusInSetPoint(x)
                    }
                  }
                />
              </div>
              <button
                type='button'
                id={`setpointRemoveButton-${index}`}
                onClick={removeSetPoint}
                style={{
                  border: 'none',
                  background: 'inherit',
                }}
              >
                <i className='fa fa-minus-circle fa-2x' id={`setpointRemoveButtonI-${index}`} style={{ paddingLeft: '5px' }} />
              </button>
              {index >= (array.length - 1)
                ? setPoints.length < 8 && (
                  <button
                    type='button'
                    onClick={addNewSetPoint}
                    style={{
                      border: 'none',
                      background: 'inherit',
                    }}
                  >
                    <i className='fa fa-plus-circle fa-2x' aria-hidden='true' style={{ paddingLeft: '5px' }} />
                  </button>
                )
                : <div />}
            </li>
          ))
        }
      </ul>
    </div>
  )
}

const MoistureCombinedSettings = ({ device, saved }) => {
  const {
    physical: {
      moistureSensorSettings: {
        moistureCombined: {
          goalMax: goalMaxP,
          goalMin: goalMinP,
          setPoints: setPointsP,
          setPointsAreVisible: setPointsAreVisibleP,
        } = {},
      } = {},
    } = {},
  } = device

  const [goalMax, setGoalMax] = useState(goalMaxP)
  const [goalMin, setGoalMin] = useState(goalMinP)
  const [goalValid, setGoalValid] = useState(true)
  const [setPoints, setSetPoints] = useState(setPointsP)
  const [setPointsAreVisible, setSetPointsAreVisible] = useState(setPointsAreVisibleP)
  const [saveCategory, setSaveCategory] = useState(undefined)
  const [firstRun, setFirstRun] = useState(true)

  /**
   * Set window focus to an element after a short delay.
   * @param {string} id
   */
  const focusInElement = (id) => {
    window.setTimeout(() => {
      document.getElementById(id).focus()
    }, 5)
  }

  useEffect(
    () => {
      const {
        physical: {
          moistureSensorSettings: {
            moistureCombined = {},
          } = {},
        } = {},
      } = device
      const newSettings = { ...moistureCombined }

      if (firstRun) {
        setFirstRun(false)
        return
      }
      if (goalMax > goalMin) {
        setGoalValid(true)
        newSettings.goalMax = goalMax
        newSettings.goalMin = goalMin
      } else {
        setGoalValid(false)
        return
      }
      newSettings.setPoints = setPoints
      newSettings.setPointsAreVisible = setPointsAreVisible

      const deviceID = device.id

      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }

      debounceTimeout = setTimeout(() => {
        dispatch('set-physical', { deviceID, data: { moistureSensorSettings: { moistureCombined: newSettings } } })
      }, debounceMilliseconds)
    },
    [
      goalMax,
      goalMin,
      setPoints,
      setPointsAreVisible,
    ]
  )

  const handleChange = (event, setter) => {
    const { target: { id, max, min, valueAsNumber, name } } = event
    let value = valueAsNumber

    // value can't be < min
    if ((value || value === 0) && value < Number(min)) value = Number(min)

    // value can't be > max
    if (value > Number(max)) value = Number(max)

    switch (name) {
      case 'goalMin':
      case 'goalMax':
        setSaveCategory('goals')
        break
      case 'viewDays':
        setSaveCategory('view')
        break
      default:
        break
    }

    setter(value)
    if (id) focusInElement(id)
  }

  const handleSetpointsChange = (value) => {
    setSaveCategory('setPoints')
    setSetPoints(value)
  }

  const hideSetPoints = () => {
    setSaveCategory('setPoints')
    setSetPointsAreVisible(false)
  }

  const showSetPoints = () => {
    setSaveCategory('setPoints')
    setSetPointsAreVisible(true)
  }

  const cyan = '#008B8B'
  const red = '#D00000'

  const toggleButtonStyle = {
    border: 'none',
    background: 'inherit',
    paddingLeft: '6px',
    display: 'flex',
    marginTop: '10px',
  }

  const iconStyle = {
    fontSize: '30px',
    margin: '12px 0px 0px 0px',
    padding: '10px 10px 0px',
  }

  const offButton = clickHandler => (
    <div>
      <button
        type='button'
        onClick={() => clickHandler()}
        style={{
          ...toggleButtonStyle,
          color: red,
        }}
      >
        <FormattedMessage
          id='device.machineState.off'
          defaultMessage='OFF'
          description='off label'
          tagName='h3'
        />
        <i className='fa fa-toggle-off' style={iconStyle} />
      </button>
    </div>
  )

  const onButton = clickHandler => (
    <div style={{ display: 'flex', color: cyan }}>
      <button
        type='button'
        onClick={() => clickHandler()}
        style={{
          ...toggleButtonStyle,
          color: cyan,
        }}
      >
        <FormattedMessage
          id='device.machineState.on'
          defaultMessage='ON'
          description='on label'
          tagName='h3'
        />
        <i className='fa fa-toggle-on' style={iconStyle} />
      </button>
    </div>
  )

  let width = 288

  if (window.innerWidth >= 600) {
    width = 612
  } else if (window.innerWidth >= 375) {
    width = 360
  }

  const visible = saved !== false
  const outcome = saved !== true ? 'error' : 'success'

  return (
    <div className={styles.pageWidthFull}>
      <StatusSave visible={visible} outcome={outcome} />
      <form>
        <section>
          <div style={{ display: 'inline-flex', width, paddingLeft: '10px' }}>
            <div style={{ width: '90%' }}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <FormattedMessage
                  id='device.moisture.goals.heading'
                  defaultMessage='Goals'
                  description='moisture goals heading'
                  tagName='h2'
                />
                {saveCategory === 'goals' && <StatusSaveCheck visible={visible} outcome={outcome} />}
              </div>
            </div>
            <div style={{ display: 'inline-block', textAlign: 'right' }}>
              {/* goalsAreVisible
                ? onButton(hideGoals)
                : offButton(showGoals)
              */}
            </div>
          </div>
          <div className={stylesSettings.inputArea} style={{ borderRadius: '10px', padding: '10px', margin: '0 10px', width: 'inherit' }}>
            <FormattedMessage
              id='general.minimum'
              defaultMessage='Minimum'
              description='Minimum label'
              tagName='h5'
            />
            <div className={goalValid ? stylesSettings.otherNumber : `${stylesSettings.otherNumber} ${stylesSettings.inputIssue}`}>
              <NumberInput
                value={goalMin}
                childChange={event => handleChange(event, setGoalMin)}
                field='goalMin'
                min={0}
                max={100}
                steps={1}
                style={stylesSettings.otherNumberInput}
                id='moisture-combined-settings-page-goalMin'
              />
            </div>
            <FormattedMessage
              id='general.maximum'
              defaultMessage='Maximum'
              description='Maximum label'
              tagName='h5'
            />
            <div className={goalValid ? stylesSettings.otherNumber : `${stylesSettings.otherNumber} ${stylesSettings.inputIssue}`}>
              <NumberInput
                value={goalMax}
                childChange={event => handleChange(event, setGoalMax)}
                field='goalMax'
                min={0}
                max={100}
                steps={1}
                style={stylesSettings.otherNumberInput}
                id='moisture-combined-settings-page-goalMax'
              />
            </div>
          </div>
        </section>
        <section>
          <div style={{ display: 'flex', width: '100%', paddingLeft: '10px', paddingRight: '10px', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <FormattedMessage
                  id='device.moisture.setpoints.heading'
                  defaultMessage='Set Points'
                  description='Moisture Set Points Heading'
                  tagName='h2'
                />
                {saveCategory === 'setPoints' && <StatusSaveCheck visible={visible} outcome={outcome} />}
              </div>
            </div>
            <span style={{ textAlign: 'right' }}>
              {setPointsAreVisible
                ? onButton(hideSetPoints)
                : offButton(showSetPoints)}
            </span>
          </div>
          <div className={stylesSettings.inputArea} style={{ borderRadius: '10px', padding: '10px', margin: '0 10px', width: 'inherit' }}>
            <MoistureSetPoints
              setPoints={setPoints}
              changeHandler={value => handleSetpointsChange(value)}
            />
          </div>
        </section>
        {/* <section>
          <div style={{ display: 'flex', flexDirection: 'row', paddingLeft: '10px' }}>
            <FormattedMessage
              id='chart.view.heading'
              defaultMessage='View'
              description='View Heading'
              tagName='h2'
            />
            {saveCategory === 'view' && <StatusSaveCheck visible={visible} outcome={outcome} />}
          </div>
          <div className={stylesSettings.inputArea} style={{ borderRadius: '10px', padding: '10px', margin: '0 10px', width: 'inherit' }}>
            <FormattedMessage
              id='chart.viewDays.label'
              defaultMessage='View Days'
              description='View Days label'
              tagName='h5'
            />
            <div className={stylesSettings.otherNumber}>
              <NumberInput
                field='viewDays'
                childChange={event => handleChange(event, setViewDays)}
                min={1}
                max={125}
                step={1}
                style={stylesSettings.otherNumberInput}
                value={viewDays}
              />
            </div>
          </div>
        </section> */}
      </form>
    </div>
  )
}

function MoistureCombinedSettingsPage (props) {
  const navigate = useNavigate()
  const { customerID, deviceID } = useParams()

  const [state, setState] = React.useState({
    saved: props.data?.deviceSettings?.saved || false,
  })

  const statusUpdateOnce = React.useRef(true)

  const appStoreChangeListener = React.useCallback(() => {
    const { data: { deviceSettings: { saved } } } = AppStore
    setState(prevState => ({ ...prevState, saved }))
  }, [])

  const backToDevice = React.useCallback(() => {
    navigate(`/${customerID}/moistureSensor/${deviceID}`)
  }, [customerID, deviceID, navigate])

  const getNewData = React.useCallback(() => {
    dispatch('get-device', { deviceID })
    AppStore.addChangeListener(appStoreChangeListener)
  }, [deviceID, appStoreChangeListener])

  React.useEffect(() => {
    getNewData()

    return () => {
      // clearInterval(interval)
      dispatch('unload-device-status')
      dispatch('reset-new-settings')
      AppStore.removeChangeListener(appStoreChangeListener)
    }
  }, [getNewData, appStoreChangeListener])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device && statusUpdateOnce.current) {
      statusUpdateOnce.current = false
      const status = statusCalculate(device)
      dispatch('load-device-status', { status })
      dispatch('initialize-new-settings', { device })
    }
  }, [props.data?.lookupReady, props.data?.lookup, deviceID])

  const {
    auth: { logout },
    data: {
      lookupReady,
      lookup,
    } = {},
    pages: {
      MoistureCombinedSettingsPage: title = '',
    },
    route: {
      path,
    } = {},
  } = props

  const { saved } = state
  const device = lookupReady && lookup[deviceID]
  const {
    address_alias: addressAlias,
    physical: {
      deviceNumber,
    } = {},
  } = device || {}
  const name = deviceNumber || addressAlias || ''
  const subject = device
    ? (
      <div>
        <div>
          <MoistureCombinedSettings
            device={device}
            saved={saved}
          />
        </div>
      </div>
      )
    : (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
      )

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={backToDevice}
        name={name}
        page={path}
        logOut={() => logout(navigate)}
      />
      {subject}
      <Footer />
    </div>
  )
}

MoistureCombinedSettingsPage.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(MoistureCombinedSettingsPage))
